import React, { useState, useEffect, useRef, type FC, type MutableRefObject } from 'react';
import { checkOutViewTooltip } from '@wix/bi-logger-payments-data/v2';
import { biLogger } from '../../../../../utils/biLogger';
import { cvvTooltipHideDelay } from './config';
import { type CVVHintProps } from './types';
import { CVVHintView } from './view';

export const CVVHintController: FC<CVVHintProps> = (props) => {
  const tooltipHideTimeoutRef: MutableRefObject<number> = useRef(null);
  const [wasOpenedViaClick, setWasOpenedViaClick] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>();
  const tooltipRef = useRef<HTMLDivElement>();

  const clearHideTimeout = () => {
    if (tooltipHideTimeoutRef.current !== null) {
      window.clearTimeout(tooltipHideTimeoutRef.current);
      tooltipHideTimeoutRef.current = null;
    }
  };

  const handleMouseOver = () => {
    if (isOpened) {
      if (tooltipHideTimeoutRef.current) {
        clearHideTimeout();
      }
    } else {
      setIsOpened(true);
    }
  };

  const handleMouseOut = () => {
    if (isOpened && !wasOpenedViaClick) {
      tooltipHideTimeoutRef.current = window.setTimeout(() => {
        setIsOpened(false);
        clearHideTimeout();
      }, cvvTooltipHideDelay);
    }
  };

  const handleButtonClick = () => {
    const shouldBeOpened = !isOpened;

    setIsOpened(shouldBeOpened);

    if (shouldBeOpened) {
      setWasOpenedViaClick(true);
    }
  };

  const handleDocumentClick = (e) => {
    if (!buttonRef.current?.contains(e.target) && !tooltipRef.current?.contains(e.target)) {
      setIsOpened(false);
    }
  };

  useEffect(() => {
    if (isOpened) {
      biLogger.report(
        checkOutViewTooltip({
          tooltipType: 'pci_form',
        }),
      );
    } else {
      setWasOpenedViaClick(false);
    }
  }, [isOpened]);

  useEffect(() => {
    if (isOpened) {
      document.addEventListener('mousedown', handleDocumentClick);
    } else {
      document.removeEventListener('mousedown', handleDocumentClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isOpened]);

  useEffect(() => clearHideTimeout);

  return (
    <CVVHintView
      {...props}
      isOpened={isOpened}
      buttonRef={buttonRef}
      tooltipRef={tooltipRef}
      onButtonClick={handleButtonClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    />
  );
};
CVVHintController.displayName = 'CVVHintController';
